"use client"

import * as React from "react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import {
  Boxes,
  Loader2,
  LogOut,
  Settings2,
  User,
  Workflow as WorkflowIcon,
} from "lucide-react"

import { useAuth } from "@/lib/auth"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { ThemeToggle } from "@/components/theme-toggle"

type NavItem = { href: string; label: string; icon: typeof Boxes }

const NAV: NavItem[] = [
  { href: "/skills", label: "Action Skills", icon: Boxes },
  { href: "/workflows", label: "Workflows", icon: WorkflowIcon },
]

/** One hop in the header trail. The last one renders as plain text. */
export type Crumb = { label: string; href?: string }

/**
 * Application shell — shadcn's collapsible sidebar.
 *
 * `collapsible="icon"` keeps a rail visible when collapsed rather than hiding
 * navigation entirely; labels become tooltips in that state.
 */
export function AppShell({
  crumbs,
  status,
  children,
}: {
  /**
   * Trail for the header. Navigation state lives in the URL now, so a detail
   * view is a real page: it deep-links, the back button works, and the trail
   * shows where you are instead of a single word.
   */
  crumbs: Crumb[]
  status?: React.ReactNode
  children: React.ReactNode
}) {
  const router = useRouter()
  const pathname = usePathname()
  const { user, loading, signOut } = useAuth()

  React.useEffect(() => {
    if (!loading && !user) router.replace("/login")
  }, [loading, user, router])

  if (loading || !user) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const initials = (user.email ?? "?")
    .split("@")[0]!
    .split(/[.\-_]/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("")

  return (
    <SidebarProvider defaultOpen>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" className="pointer-events-none">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <WorkflowIcon className="size-4" />
                </div>
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-sm font-semibold">Workflows</span>
                  <span className="truncate text-xs text-muted-foreground">futureofsports.io</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Build</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {NAV.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    {/* base-ui style: `render`, not `asChild`. */}
                    <SidebarMenuButton
                      render={<Link href={item.href} />}
                      // Highlight the section for its detail pages too, not
                      // only the exact list route.
                      isActive={pathname === item.href || pathname.startsWith(item.href + "/")}
                      tooltip={item.label}
                    >
                      <item.icon />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarSeparator />
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                render={<Link href="/profile" />}
                size="lg"
                isActive={pathname === "/profile"}
                tooltip={user.email ?? "Profile"}
              >
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-muted text-xs font-semibold">
                  {initials || <User className="size-4" />}
                </div>
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-sm">{user.email?.split("@")[0]}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {user.email?.split("@")[1]}
                  </span>
                </div>
                <Settings2 className="ml-auto size-4 opacity-50" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>

        {/* Drag-to-collapse edge. */}
        <SidebarRail />
      </Sidebar>

      <SidebarInset>
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between gap-3 border-b bg-background/80 px-4 backdrop-blur">
          <div className="flex min-w-0 items-center gap-2">
            <SidebarTrigger />
            <Breadcrumb className="min-w-0">
              <BreadcrumbList className="flex-nowrap">
                {crumbs.map((c, i) => {
                  const last = i === crumbs.length - 1
                  return (
                    <React.Fragment key={`${c.label}-${i}`}>
                      <BreadcrumbItem className="min-w-0">
                        {last || !c.href ? (
                          <BreadcrumbPage className="truncate">{c.label}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink render={<Link href={c.href} />}>
                            {c.label}
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                      {!last && <BreadcrumbSeparator />}
                    </React.Fragment>
                  )
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="flex items-center gap-1.5">
            {status}
            <ThemeToggle variant="ghost" size="sm" />
            <Button size="sm" variant="ghost" onClick={() => void signOut()} aria-label="Sign out">
              <LogOut className="size-3.5" />
            </Button>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-6">{children}</main>
      </SidebarInset>
    </SidebarProvider>
  )
}
